import React from 'react';
import { NumericFormat } from 'react-number-format';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { REPORT_KEYS } from '_constants/reports';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';
import { selectData } from '_store/reports/selector';

import Icon from 'components/atomic/Icon';
import Text from 'components/atomic/layouts/Text';

const StyledContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
  flex-wrap: wrap;
  margin-bottom: ${theme.spaces.regular}px;

  @media ${theme.devices.tablet} {
    gap: ${theme.spaces.smallX}px;
  }
`;

const StyledDot = styled.div`
  height: 4px;
  width: 4px;
  border-radius: 50%;
  background: ${theme.colors.gray.secondary};
`;

const StyledRateUpContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spaces.small2X}px;
  padding-top: ${theme.spaces.small2X}px;
  padding-bottom: ${theme.spaces.small2X}px;
  padding-right: ${theme.spaces.smallX}px;
  padding-left: ${theme.spaces.smallX}px;
  border: 1px solid ${hexToRGBA(theme.colors.green.primary, 0.08)};
  border-radius: 16px;
  background: ${hexToRGBA(theme.colors.green.primary, 0.08)};
`;

const StyledRateDownContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spaces.small2X}px;
  padding-top: ${theme.spaces.small2X}px;
  padding-bottom: ${theme.spaces.small2X}px;
  padding-right: ${theme.spaces.smallX}px;
  padding-left: ${theme.spaces.smallX}px;
  border: 1px solid ${hexToRGBA(theme.colors.red.primary, 0.08)};
  border-radius: 16px;
  background: ${hexToRGBA(theme.colors.red.primary, 0.08)};
`;

const TotalSection = () => {
  const data = useSelector(selectData(REPORT_KEYS.expensesGraph));

  return (
    <StyledContainer>
      <Text
        size={theme.fontSizes.largeX}
        weight={600}
        letterSpacing="-0.04em"
        lineHeight={theme.spaces.large2X}
        color={theme.colors.gray.primary}
      >
        Business Overview
      </Text>
      <StyledDot />
      <Text
        size={theme.fontSizes.largeX}
        weight={600}
        letterSpacing="-0.04em"
        lineHeight={theme.spaces.large2X}
        color={theme.colors.gray.secondary}
      >
        Annual Profit:
      </Text>
      <Text
        size={theme.fontSizes.largeX}
        weight={600}
        letterSpacing="-0.04em"
        lineHeight={theme.spaces.large2X}
        color={theme.colors.gray.primary}
        ignoreSpan
      >
        <NumericFormat
          displayType="text"
          value={data?.annualProfit}
          prefix="$"
          thousandSeparator=","
          decimalScale={0}
          fixedDecimalScale
        />
      </Text>
      {data?.annualProfitChangePercentage >= 0 && (
        <StyledRateUpContainer>
          <Text size={theme.fontSizes.smallX} weight={700} color={theme.colors.green.primary} inline ignoreSpan>
            <NumericFormat
              displayType="text"
              value={data?.annualProfitChangePercentage}
              suffix="%"
              decimalScale={1}
              fixedDecimalScale
            />
          </Text>
          <Icon alias={ICON_ALIASES.rateUp} color={theme.colors.green.primary} />
        </StyledRateUpContainer>
      )}
      {data?.annualProfitChangePercentage < 0 && (
        <StyledRateDownContainer>
          <Text size={theme.fontSizes.smallX} weight={700} color={theme.colors.red.primary} inline ignoreSpan>
            <NumericFormat
              displayType="text"
              value={data?.annualProfitChangePercentage}
              suffix="%"
              decimalScale={1}
              fixedDecimalScale
              allowNegative
            />
          </Text>
          <Icon alias={ICON_ALIASES.rateDown} color={theme.colors.red.primary} />
        </StyledRateDownContainer>
      )}
    </StyledContainer>
  );
};

export default TotalSection;
